import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

const API_BASE = process.env.REACT_APP_API_URL || 'https://norwich-hub.onrender.com';

interface Props { marketId: string; currentUser?: string; }

interface FeedItem {
  id:         string;
  user_id:    string | null;
  side:       string | null;
  amount:     number;
  created_at: string | null;
  fresh:      boolean;
}

interface ReasoningEntry {
  id:            string;
  kind:          'oracle' | 'bet';
  user_id:       string;
  market_id:     string;
  outcome_id:    string;
  outcome_label: string;
  text:          string;
  vibe:          number | null;
  created_at:    string;
}

const USER_LABEL: Record<string, string> = {
  alice:   'ALICE',
  bob:     'BOB',
  charlie: 'CHARLIE',
};

const YES_STYLE = { bg: '#14532d', text: '#86efac', border: '#166534' };
const NO_STYLE  = { bg: '#450a0a', text: '#fca5a5', border: '#7f1d1d' };
const BOT_ACCENT = '#a78bfa';

const TRADE_LOG_KEY = 'agoracle_trade_log';
const TRADE_LOG_MAX = 200;

function sideStyle(side: string | null) {
  return side === 'YES' ? YES_STYLE : NO_STYLE;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function isBot(userId: string | null): boolean {
  return !!userId && userId.startsWith('bot_');
}

function prettyBot(id: string): string {
  return id.slice(4).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function mergeTradeLog(incoming: ReasoningEntry[]): ReasoningEntry[] {
  let existing: ReasoningEntry[] = [];
  try {
    const raw = localStorage.getItem(TRADE_LOG_KEY);
    if (raw) existing = JSON.parse(raw) as ReasoningEntry[];
  } catch { /* localStorage unavailable or corrupt — fall through */ }

  const seen = new Set<string>();
  const merged: ReasoningEntry[] = [];
  for (const e of [...incoming, ...existing]) {
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    merged.push(e);
  }
  const capped = merged.slice(0, TRADE_LOG_MAX);
  try { localStorage.setItem(TRADE_LOG_KEY, JSON.stringify(capped)); } catch { /* ignore quota */ }
  return capped;
}

let _idCounter = 0;
function nextId() { return `feed-${++_idCounter}`; }

export default function ActivityFeed({ marketId, currentUser }: Props): React.JSX.Element {
  const [items,     setItems]     = useState<FeedItem[]>([]);
  const [error,     setError]     = useState<string | null>(null);
  const [reasoning, setReasoning] = useState<Record<string, ReasoningEntry>>({});
  const [expanded,  setExpanded]  = useState<Set<string>>(new Set());
  const cancelled = useRef(false);

  const fetchActivity = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch(`${API_BASE}/api/market/${marketId}/activity`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: Array<{
        user_id:          string | null;
        side:             string | null;
        amount:           number;
        created_at:       string | null;
        transaction_type: string;
      }> = await res.json();

      if (cancelled.current) return;
      setError(null);
      setItems(data.map(r => ({
        id:         nextId(),
        user_id:    r.user_id,
        side:       r.side,
        amount:     r.amount,
        created_at: r.created_at,
        fresh:      false,
      })));
    } catch (e) {
      if (!cancelled.current) setError((e as Error).message);
    }
  }, [marketId]);

  const fetchReasoning = useCallback(async (): Promise<void> => {
    try {
      const qs = new URLSearchParams({ market_id: marketId, limit: '60' });
      const res = await fetch(`${API_BASE}/api/multi/reasoning?${qs.toString()}`, { cache: 'no-store' });
      if (!res.ok) return; // endpoint absent or market has no reasoning — silent
      const data: { entries?: ReasoningEntry[] } = await res.json();
      const entries = data.entries ?? [];
      if (cancelled.current || entries.length === 0) return;

      const persisted = mergeTradeLog(entries);
      // Index latest entry per user_id for inline chevron lookup
      const byUser: Record<string, ReasoningEntry> = {};
      for (const e of persisted) {
        if (!byUser[e.user_id]) byUser[e.user_id] = e;
      }
      setReasoning(byUser);
    } catch { /* non-fatal */ }
  }, [marketId]);

  useEffect(() => {
    cancelled.current = false;
    setItems([]);
    setError(null);
    setExpanded(new Set());
    fetchActivity();
    fetchReasoning();

    const socket = io(API_BASE);

    socket.on('new_bet', (data: {
      market_id:  string;
      user_id:    string;
      side:       string;
      amount:     number;
      created_at: string;
    }) => {
      if (cancelled.current || data.market_id !== marketId) return;
      const entry: FeedItem = {
        id:         nextId(),
        user_id:    data.user_id,
        side:       data.side,
        amount:     data.amount,
        created_at: data.created_at,
        fresh:      true,
      };
      setItems(prev => [entry, ...prev].slice(0, 30));
      setTimeout(() => {
        setItems(prev => prev.map(it => it.id === entry.id ? { ...it, fresh: false } : it));
      }, 600);
    });

    socket.on('multi_bet',   fetchReasoning);
    socket.on('vibe_update', fetchReasoning);

    return () => { cancelled.current = true; socket.disconnect(); };
  }, [fetchActivity, fetchReasoning, marketId]);

  function toggleExpanded(itemId: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  return (
    <div
      style={{
        backgroundColor: '#0f172a',
        border:          '1px solid #1e293b',
        borderRadius:    8,
        padding:         '10px 14px',
        width:           '100%',
        fontFamily:      'monospace',
      }}
    >
      <style>{`
        @keyframes feedSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .feed-fresh { animation: feedSlideIn 0.35s ease-out forwards; }
        @keyframes whimsyExpand {
          from { opacity: 0; max-height: 0;   }
          to   { opacity: 1; max-height: 200px; }
        }
        .whimsy-expand { animation: whimsyExpand 0.22s ease-out forwards; overflow: hidden; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <p style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.22em', margin: 0 }}>
          ACTIVITY FEED
        </p>
        <span style={{
          color: '#22c55e', fontSize: 7, letterSpacing: '0.18em',
          border: '1px solid #166534', borderRadius: 3, padding: '1px 5px',
        }}>
          LIVE
        </span>
      </div>

      {/* Scrollable ticker */}
      <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        {error && (
          <p style={{ color: 'rgba(239,68,68,0.6)', fontSize: 10, margin: 0 }}>{error}</p>
        )}
        {!error && items.length === 0 && (
          <p style={{ color: '#334155', fontSize: 10, margin: 0, textAlign: 'center', padding: '8px 0' }}>
            No activity yet.
          </p>
        )}

        {items.map(item => {
          const ss         = sideStyle(item.side);
          const isMe       = item.user_id === currentUser;
          const bot        = isBot(item.user_id);
          const displayName = bot && item.user_id
            ? prettyBot(item.user_id)
            : item.user_id ? (USER_LABEL[item.user_id] ?? item.user_id.toUpperCase()) : null;
          const verb       = item.side ? (item.side === 'YES' ? 'bought YES' : 'bought NO') : 'activity';
          const whimsy     = bot && item.user_id ? reasoning[item.user_id] : undefined;
          const isExpanded = expanded.has(item.id);

          return (
            <div key={item.id} className={item.fresh ? 'feed-fresh' : ''}>
              <div
                style={{
                  display:         'flex',
                  alignItems:      'center',
                  justifyContent:  'space-between',
                  padding:         '5px 8px',
                  borderRadius:    whimsy && isExpanded ? '4px 4px 0 0' : 4,
                  backgroundColor: isMe ? '#0c1a2e' : bot ? '#130d24' : '#0f172a',
                  border:          `1px solid ${isMe ? '#1e3a5a' : bot ? '#2d1b69' : '#1e293b'}`,
                  borderBottom:    whimsy && isExpanded ? '1px solid #2d1b69' : undefined,
                }}
              >
                {/* Left: user badge + action + optional chevron */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  {displayName && (
                    <span style={{
                      fontSize:        bot ? 11 : 8,
                      fontWeight:      bot ? 500 : 700,
                      letterSpacing:   bot ? '0.02em' : '0.12em',
                      padding:         '1px 6px',
                      borderRadius:    3,
                      backgroundColor: isMe ? '#0d3a5a' : bot ? '#1e1040' : '#111820',
                      color:           isMe ? '#7ab8e8' : bot ? BOT_ACCENT : '#4b6080',
                      border:          `1px solid ${isMe ? '#2a7ab8' : bot ? '#4c1d95' : '#1e2d3d'}`,
                      whiteSpace:      'nowrap',
                      flexShrink:      0,
                      fontFamily:      bot ? "'Cormorant Garamond', serif" : 'monospace',
                      fontStyle:       bot ? 'italic' : 'normal',
                      textTransform:   bot ? 'none' : 'uppercase',
                    }}>
                      {displayName}
                    </span>
                  )}
                  {item.side && (
                    <span style={{ color: ss.text, fontSize: 11 }}>
                      {verb}
                    </span>
                  )}
                  {whimsy && (
                    <button
                      onClick={() => toggleExpanded(item.id)}
                      title="Show Signal Analysis from the 13 Wards"
                      aria-expanded={isExpanded}
                      style={{
                        background:    'none',
                        border:        `1px solid ${isExpanded ? '#7c3aed' : '#2d1b69'}`,
                        borderRadius:  3,
                        color:         isExpanded ? '#c084fc' : '#7c3aed',
                        cursor:        'pointer',
                        fontFamily:    'monospace',
                        fontSize:      9,
                        padding:       '0 5px',
                        lineHeight:    1.2,
                        marginLeft:    2,
                        transition:    'color 0.15s, border-color 0.15s',
                      }}>
                      {isExpanded ? '▾' : '▸'} SIGNAL
                    </button>
                  )}
                </div>

                {/* Right: amount + side pill + time */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: ss.text }}>
                    ${item.amount.toFixed(2)}
                  </span>
                  {item.side && (
                    <span style={{
                      fontSize:        8,
                      fontWeight:      700,
                      letterSpacing:   '0.1em',
                      padding:         '1px 5px',
                      borderRadius:    3,
                      backgroundColor: ss.bg,
                      color:           ss.text,
                      border:          `1px solid ${ss.border}`,
                    }}>
                      {item.side}
                    </span>
                  )}
                  <span style={{ color: '#2a3a4a', fontSize: 9 }}>
                    {formatTime(item.created_at)}
                  </span>
                </div>
              </div>

              {whimsy && isExpanded && (
                <div className="whimsy-expand" style={{
                  padding:         '10px 14px 12px',
                  borderRadius:    '0 0 4px 4px',
                  backgroundColor: '#0b0820',
                  backgroundImage: 'linear-gradient(180deg, rgba(124,58,237,0.06) 0%, transparent 100%)',
                  border:          '1px solid #2d1b69',
                  borderTop:       'none',
                }}>
                  <div style={{
                    display:       'flex',
                    alignItems:    'center',
                    gap:           8,
                    marginBottom:  6,
                  }}>
                    <span style={{
                      fontFamily:    "'Cormorant Garamond', serif",
                      fontStyle:     'italic',
                      fontSize:      11,
                      color:         '#fcd34d',
                      lineHeight:    1,
                    }}>
                      ◈
                    </span>
                    <span style={{
                      color:         '#c084fc',
                      fontSize:      8,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      fontWeight:    600,
                    }}>
                      Signal from the Ward
                    </span>
                    <span style={{ color: '#2d1b69', fontSize: 9 }}>·</span>
                    <span style={{
                      color:         '#7c3aed',
                      fontSize:      8,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                    }}>
                      {whimsy.outcome_label}
                    </span>
                    {whimsy.vibe !== null && (
                      <>
                        <span style={{ color: '#2d1b69', fontSize: 9 }}>·</span>
                        <span style={{ color: '#f59e0b', fontSize: 9, letterSpacing: '0.14em', fontFamily: 'monospace' }}>
                          VIBE {(whimsy.vibe * 100).toFixed(0)}%
                        </span>
                      </>
                    )}
                  </div>
                  <p style={{
                    color:      '#e8ecf5',
                    fontSize:   14,
                    lineHeight: 1.65,
                    margin:     '0 0 0 2px',
                    paddingLeft:10,
                    borderLeft: '2px solid #4c1d95',
                    fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                    fontStyle:  'italic',
                    fontWeight: 400,
                  }}>
                    “{whimsy.text}”
                  </p>
                  <p style={{
                    margin:       '6px 0 0 12px',
                    color:        '#475569',
                    fontFamily:   'monospace',
                    fontSize:     8,
                    letterSpacing:'0.18em',
                  }}>
                    — Signal Analysis from the 13 Wards · Sentinel of the Ward
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
